import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAllOrders,
  getUserOrders,
  addOrder,
  updateOrder,
  deleteOrder,
  updateProductStock,
  getOrdersForMonth,
} from '../../services/orderService';

// Async thunks
export const fetchOrders = createAsyncThunk('order/fetchOrders', async (monthKey, { rejectWithValue }) => {
  try {
    const orders = monthKey ? await getOrdersForMonth(monthKey) : await getAllOrders();
    return orders;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const fetchUserOrders = createAsyncThunk(
  'order/fetchUserOrders',
  async (userId, { rejectWithValue }) => {
    try {
      const orders = await getUserOrders(userId);
      return orders;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createOrder = createAsyncThunk(
  'order/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const orderId = await addOrder(orderData);

      // Update product stock for each item in the order
      for (const item of orderData.items) {
        await updateProductStock(item.productId, item.quantity);
      }

      return { id: orderId, ...orderData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateOrderById = createAsyncThunk(
  'order/updateOrder',
  async ({ orderId, monthKey, dayKey, orderData }, { rejectWithValue }) => {
    try {
      await updateOrder(orderId, monthKey, dayKey, orderData);
      return { id: orderId, monthKey, dayKey, ...orderData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteOrderById = createAsyncThunk(
  'order/deleteOrder',
  async ({ orderId, monthKey, dayKey }, { rejectWithValue }) => {
    try {
      await deleteOrder(orderId, monthKey, dayKey);
      return orderId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  orders: [],
  loading: false,
  error: null,
  success: false,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
        state.error = null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch orders';
      })
      // Fetch user orders
      .addCase(fetchUserOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
        state.error = null;
      })
      .addCase(fetchUserOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user orders';
      })
      // Create order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.unshift(action.payload);
        state.error = null;
        state.success = true;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create order';
        state.success = false;
      })
      // Update order
      .addCase(updateOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateOrderById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex((order) => order.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        state.error = null;
        state.success = true;
      })
      .addCase(updateOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update order';
        state.success = false;
      })
      // Delete order
      .addCase(deleteOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders.filter((order) => order.id !== action.payload);
        state.error = null;
        state.success = true;
      })
      .addCase(deleteOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete order';
        state.success = false;
      });
  },
});

export const { clearMessages } = orderSlice.actions;
export default orderSlice.reducer;
